import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Edit, Trash2, Clock, Plus, CornerDownRight, ChevronDown, ChevronRight, MessageSquare, Send } from 'lucide-react';

const priorities = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Medium' },
  { id: 'high', label: 'High' },
  { id: 'urgent', label: 'Urgent' }
];

const taskStatuses = [
  { id: 'not_started', label: 'Not Started' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'on_hold', label: 'On Hold' },
  { id: 'under_review', label: 'Under Review' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'completed', label: 'Completed' }
];

const selectableStatuses = taskStatuses.filter(s => s.id !== 'overdue');

export const CreateTaskDialog = ({ open, onOpenChange, form, setForm, onSubmit, parentTasks, teamMembers }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Create New Task</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Create as Subtask of (Optional)</label>
          <Select value={form.parent_task_id || '__none__'} onValueChange={(value) => setForm({ ...form, parent_task_id: value === '__none__' ? '' : value })}>
            <SelectTrigger>
              <SelectValue placeholder="Create as parent task" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Create as main task</SelectItem>
              {parentTasks.map((t) => (
                <SelectItem key={t.task_id} value={t.task_id}>
                  <div className="flex items-center gap-2">
                    <CornerDownRight className="h-3 w-3" />
                    {t.title}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">Select a parent task to create this as a subtask</p>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Task Title</label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Enter task title" required data-testid="task-title-input" />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Description</label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Task description" rows={3} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Priority</label>
            <Select value={form.priority} onValueChange={(value) => setForm({ ...form, priority: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {priorities.map((p) => (<SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Assign To</label>
            <Select value={form.assigned_to} onValueChange={(value) => setForm({ ...form, assigned_to: value })}>
              <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                {teamMembers.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Start Date</label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">End Date</label>
            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Reviewer (Optional)</label>
          <Select value={form.reviewer_id} onValueChange={(value) => setForm({ ...form, reviewer_id: value })}>
            <SelectTrigger><SelectValue placeholder="Select reviewer" /></SelectTrigger>
            <SelectContent>
              {teamMembers.filter(m => ['admin', 'manager', 'team_lead'].includes(m.role)).map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.name} ({m.role})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" data-testid="submit-task-btn">Create Task</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const CreateSubtaskDialog = ({ open, onOpenChange, form, setForm, onSubmit, parentTaskTitle, teamMembers }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Create Subtask for "{parentTaskTitle}"</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Subtask Title</label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Enter subtask title" required />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Description</label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Subtask description" rows={2} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Priority</label>
            <Select value={form.priority} onValueChange={(value) => setForm({ ...form, priority: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {priorities.map((p) => (<SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Assign To</label>
            <Select value={form.assigned_to} onValueChange={(value) => setForm({ ...form, assigned_to: value })}>
              <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                {teamMembers.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Reviewer</label>
          <Select value={form.reviewer_id || '__none__'} onValueChange={(value) => setForm({ ...form, reviewer_id: value === '__none__' ? '' : value })}>
            <SelectTrigger><SelectValue placeholder="No reviewer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No Reviewer</SelectItem>
              {teamMembers.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Start Date</label>
            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Due Date</label>
            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit">Create Subtask</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const EditProjectDialog = ({ open, onOpenChange, form, setForm, onSubmit, proposals, companies, teamMembers, canSeeBudget, clients }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Edit Project</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Linked Proposal</label>
          <Select value={form.proposal_id || '__none__'} onValueChange={(value) => setForm({ ...form, proposal_id: value === '__none__' ? '' : value })}>
            <SelectTrigger><SelectValue placeholder="Select proposal (optional)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">-- No Proposal --</SelectItem>
              {proposals.map((p) => (
                <SelectItem key={p.proposal_id} value={p.proposal_id}>{p.title} ({p.client_name}) - {p.status?.replace('_', ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Project Name *</label>
          <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Enter project name" required />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Project Code</label>
          <Input value={form.project_code || ''} onChange={(e) => setForm({ ...form, project_code: e.target.value })} placeholder="e.g. PRJ-001" />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Description</label>
          <Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Company</label>
            <Select value={form.company_id || '__none__'} onValueChange={(value) => setForm({ ...form, company_id: value === '__none__' ? '' : value })}>
              <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">-- No Company --</SelectItem>
                {companies.map((c) => (<SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Contact Name</label>
            <Select value={form.client_name || '__none__'} onValueChange={(value) => setForm({ ...form, client_name: value === '__none__' ? '' : value })}>
              <SelectTrigger><SelectValue placeholder="Select contact" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">-- No Contact --</SelectItem>
                {(clients || []).map((c) => (<SelectItem key={c.client_id} value={c.name}>{c.name} {c.company_name ? `(${c.company_name})` : ''}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {canSeeBudget && (
        <div>
          <label className="text-sm font-medium mb-2 block">Budget (INR)</label>
          <Input type="number" value={form.budget || ''} onChange={(e) => setForm({ ...form, budget: parseFloat(e.target.value) || null })} placeholder="0.00" />
        </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Start Date</label>
            <Input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">End Date</label>
            <Input type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Status</label>
          <Select value={form.status || 'active'} onValueChange={(value) => setForm({ ...form, status: value })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on_hold">On Hold</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Project Lead</label>
            <Select value={form.project_lead || '__none__'} onValueChange={(v) => setForm({ ...form, project_lead: v === '__none__' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Select lead" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">-- None --</SelectItem>
                {teamMembers.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Project Associate</label>
            <Select value={form.project_associate || '__none__'} onValueChange={(v) => setForm({ ...form, project_associate: v === '__none__' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="Select associate" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">-- None --</SelectItem>
                {teamMembers.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit">Save Changes</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const EditTaskDialog = ({ open, onOpenChange, form, setForm, onSubmit, teamMembers }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>Edit Task</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Task Title</label>
          <Input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Description</label>
          <Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Priority</label>
            <Select value={form.priority || 'medium'} onValueChange={(value) => setForm({ ...form, priority: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {priorities.map((p) => (<SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Status</label>
            <Select value={form.status || 'not_started'} onValueChange={(value) => setForm({ ...form, status: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {selectableStatuses.map((s) => (<SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Assign To</label>
            <Select value={form.assigned_to || '__none__'} onValueChange={(value) => setForm({ ...form, assigned_to: value === '__none__' ? '' : value })}>
              <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {teamMembers.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Reviewer</label>
            <Select value={form.reviewer_id || '__none__'} onValueChange={(value) => setForm({ ...form, reviewer_id: value === '__none__' ? '' : value })}>
              <SelectTrigger><SelectValue placeholder="No reviewer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No reviewer</SelectItem>
                {teamMembers.filter(m => ['admin', 'manager', 'team_lead'].includes(m.role)).map((m) => (
                  <SelectItem key={m.user_id} value={m.user_id}>{m.name} ({m.role})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Start Date</label>
            <Input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Due Date</label>
            <Input type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit">Save Changes</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const TaskDetailDialog = ({ open, onOpenChange, task, teamMembers, subtasks, comments, commentText, setCommentText, onAddComment, onStatusChange, onEditTask, onDeleteTask, onCreateSubtask, getStatusBadge, getPriorityBadge, formatTime }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <div className="flex items-center justify-between">
          <DialogTitle className="flex items-center gap-2">
            {task?.title}
            {task && getStatusBadge(task.status)}
          </DialogTitle>
          {task && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onEditTask(task)}>
                <Edit className="mr-1 h-3 w-3" />Edit
              </Button>
              <Button size="sm" variant="destructive" onClick={() => onDeleteTask(task.task_id)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      </DialogHeader>
      {task && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Priority</p>
              {getPriorityBadge(task.priority)}
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Assigned To</p>
              <p className="font-medium">{teamMembers.find(m => m.user_id === task.assigned_to)?.name || 'Unassigned'}</p>
            </div>
            {task.start_date && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Start Date</p>
                <p className="font-medium">{task.start_date}</p>
              </div>
            )}
            {task.end_date && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Due Date</p>
                <p className="font-medium">{task.end_date}</p>
              </div>
            )}
            {task.total_tracked_time > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Time Tracked</p>
                <p className="font-medium flex items-center gap-1"><Clock className="h-4 w-4" />{formatTime(task.total_tracked_time)}</p>
              </div>
            )}
            {task.reviewer_id && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Reviewer</p>
                <p className="font-medium">{teamMembers.find(m => m.user_id === task.reviewer_id)?.name || 'Unknown'}</p>
              </div>
            )}
          </div>
          {task.description && (
            <div>
              <p className="text-sm text-muted-foreground mb-1">Description</p>
              <p className="text-sm bg-muted p-3 rounded-lg">{task.description}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-muted-foreground mb-2">Update Status</p>
            <div className="flex gap-2 flex-wrap">
              {selectableStatuses.map((s) => (
                <Button key={s.id} size="sm" variant={task.status === s.id ? 'default' : 'outline'} onClick={() => onStatusChange(task.task_id, s.id)} data-testid={`status-btn-${s.id}`}>
                  {s.label}
                </Button>
              ))}
            </div>
          </div>
          {subtasks && subtasks.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2">Subtasks ({subtasks.length})</p>
              <div className="space-y-2">
                {subtasks.map((st) => (
                  <div key={st.task_id} className="flex items-center justify-between p-2 bg-muted rounded">
                    <span className="text-sm">{st.title}</span>
                    {getStatusBadge(st.status)}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <Button size="sm" variant="outline" onClick={() => onCreateSubtask(task)}>
              <Plus className="mr-1 h-3 w-3" />Add Subtask
            </Button>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-2">Comments ({comments.length})</p>
            <div className="space-y-3 max-h-48 overflow-y-auto mb-3">
              {comments.map((c) => (
                <div key={c.comment_id} className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium">{teamMembers.find(m => m.user_id === c.user_id)?.name || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()}</p>
                  </div>
                  <p className="text-sm">{c.content}</p>
                </div>
              ))}
              {comments.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">No comments yet</p>}
            </div>
            <div className="flex gap-2">
              <Input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Add a comment..." className="flex-1" data-testid="comment-input" />
              <Button size="sm" onClick={onAddComment} disabled={!commentText.trim()} data-testid="submit-comment-btn">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const BulkEditTasksDialog = ({ open, onOpenChange, count, form, setForm, onSubmit, teamMembers }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Bulk Edit {count} Task(s)</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Assign To</label>
          <Select value={form.assigned_to || '__none__'} onValueChange={(v) => setForm(prev => ({ ...prev, assigned_to: v === '__none__' ? '' : v }))}>
            <SelectTrigger><SelectValue placeholder="No change" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No change</SelectItem>
              {teamMembers.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium mb-2 block">Reviewer</label>
          <Select value={form.reviewer_id || '__none__'} onValueChange={(v) => setForm(prev => ({ ...prev, reviewer_id: v === '__none__' ? '' : v }))}>
            <SelectTrigger><SelectValue placeholder="No change" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No change</SelectItem>
              {teamMembers.filter(m => ['admin', 'manager', 'team_lead'].includes(m.role)).map((m) => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} data-testid="bulk-edit-submit">Apply Changes</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const DeleteProjectDialog = ({ open, onOpenChange, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete Project</AlertDialogTitle>
        <AlertDialogDescription>
          This will permanently delete the project and all its associated tasks. This action cannot be undone.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">
          Delete
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
